import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX } from "react-icons/hi";

import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import uploadDropzoneGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";

import {
  generateOppositionApplicationDetailsPresignedUrls,
  getOppositionApplicationDetailsFromClient,
  saveOppositionApplicationDetailsFromClient,
  type OppositionApplicationDetailsDocument,
} from "../../api/opposition";

export const OPPOSITION_APPLICATION_DETAILS_FORM_ID =
  "opposition-application-details-from-client-form";

const ACCEPTED_TYPES = ".jpg,.jpeg,.png,.webp,.pdf,.mp4";
const ACCEPTED_HELP = "JPEG, PNG, PDF, and MP4 formats, up to 50MB";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionApplicationDetailsFromClientView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);

  const [savedDocuments, setSavedDocuments] = useState<OppositionApplicationDetailsDocument[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
    revokeOnClose?: boolean;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!leadId || loadedRef.current) return;
    loadedRef.current = true;

    let cancelled = false;
    setLoading(true);

    getOppositionApplicationDetailsFromClient(leadId)
      .then((data) => {
        if (cancelled) return;
        setSavedDocuments(data.documents ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err instanceof Error ? err.message : "Failed to load application details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const onFilesSelected = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    const accepted: File[] = [];
    for (const file of arr) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`${file.name} exceeds the 50MB limit`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length > 0) {
      setPendingFiles((prev) => [...prev, ...accepted]);
    }
  };

  const removePendingFile = (idx: number) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  const removeSavedDocument = (idx: number) =>
    setSavedDocuments((prev) => prev.filter((_, i) => i !== idx));

  const openPendingPreview = (file: File) => {
    const url = URL.createObjectURL(file);
    setPreview({
      url,
      name: file.name,
      kind: detectPreviewKind(file.name, file.type),
      revokeOnClose: true,
    });
  };

  const openSavedPreview = (doc: OppositionApplicationDetailsDocument) => {
    if (!doc.fileUrl) {
      toast.error("Preview is not available for this file");
      return;
    }
    setPreview({ url: doc.fileUrl, name: doc.fileName, kind: detectPreviewKind(doc.fileName) });
  };

  const closePreview = () => {
    if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const totalFileCount = savedDocuments.length + pendingFiles.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;

    if (totalFileCount === 0) {
      toast.error("Please upload at least one document");
      return;
    }

    setSaving(true);
    try {
      let uploadedDocs: Array<{ fileKey: string; fileName: string }> = [];

      if (pendingFiles.length > 0) {
        const presigned = await generateOppositionApplicationDetailsPresignedUrls(
          leadId,
          pendingFiles.map((f) => ({
            filename: f.name,
            mimeType: f.type || "application/octet-stream",
          }))
        );

        if (presigned.length !== pendingFiles.length) {
          throw new Error("Failed to generate upload URLs");
        }

        await Promise.all(
          pendingFiles.map(async (file, i) => {
            const target = presigned[i];
            const putRes = await fetch(target.url, {
              method: "PUT",
              body: file,
              headers: { "Content-Type": file.type || "application/octet-stream" },
            });
            if (!putRes.ok) throw new Error(`Failed to upload ${file.name}`);
          })
        );

        uploadedDocs = pendingFiles.map((f, i) => ({
          fileKey: presigned[i].key,
          fileName: f.name,
        }));
      }

      const documents = [
        ...savedDocuments.map((d) => ({ fileKey: d.fileKey, fileName: d.fileName })),
        ...uploadedDocs,
      ];

      await saveOppositionApplicationDetailsFromClient(leadId, { documents });

      if (uploadedDocs.length > 0) {
        setSavedDocuments((prev) => [
          ...prev,
          ...uploadedDocs.map((u) => ({
            id: "",
            fileKey: u.fileKey,
            fileName: u.fileName,
            fileUrl: null,
          })),
        ]);
        setPendingFiles([]);
      }

      toast.success("Application details saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save application details");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12">
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_APPLICATION_DETAILS_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex flex-col gap-1">
          <p
            className="font-semibold text-[#101111]"
            style={{ fontSize: "13px", lineHeight: "100%", letterSpacing: "0.01em" }}
          >
            Upload Documents
          </p>
          <p
            className="font-normal text-[#7D7D7D]"
            style={{ fontSize: "11px", lineHeight: "20px", letterSpacing: "0.04em" }}
          >
            Choose a file and upload securely to proceed.
          </p>
        </div>

        {/* Dropzone */}
        <div
          className={`self-stretch flex flex-row justify-between items-center gap-5 px-5 py-4 rounded-[16px] border border-dashed bg-white transition-colors cursor-pointer ${
            dragging ? "border-[#222222] bg-[#F5F5F5]" : "border-[#D8DAEB]"
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (saving) return;
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              onFilesSelected(e.dataTransfer.files);
            }
          }}
          onClick={() => {
            if (saving) return;
            fileInputRef.current?.click();
          }}
        >
          <div className="flex flex-row items-center gap-4 min-w-0">
            <img
              src={uploadDropzoneGraphic}
              alt=""
              width={48}
              height={48}
              className="flex-shrink-0"
            />
            <div className="flex flex-col gap-1 min-w-0">
              <p
                className="font-medium text-[#101111]"
                style={{ fontSize: "14px", lineHeight: "100%", letterSpacing: "0.01em" }}
              >
                Drag and drop your files
              </p>
              <p
                className="font-normal text-[#7D7D7D]"
                style={{ fontSize: "12px", lineHeight: "18px" }}
              >
                {ACCEPTED_HELP}
              </p>
            </div>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
              fontSize: "13px",
              fontWeight: 500,
              lineHeight: "16px",
              letterSpacing: "0.04em",
            }}
          >
            Select File
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              onFilesSelected(e.target.files);
            }
            e.currentTarget.value = "";
          }}
        />

        {/* File list */}
        {totalFileCount > 0 && (
          <div className="flex flex-col gap-2 self-stretch">
            {savedDocuments.map((doc, idx) => (
              <div
                key={`saved-${doc.fileKey}-${idx}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]"
              >
                <span className="text-xs text-[#222222] truncate min-w-0 flex-1">
                  {doc.fileName}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openSavedPreview(doc)}
                    className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                  >
                    Preview
                  </button>
                  {doc.fileUrl && (
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={doc.fileName}
                      className="px-3 h-8 inline-flex items-center justify-center rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                    >
                      Download
                    </a>
                  )}
                  <button
                    type="button"
                    aria-label={`Remove ${doc.fileName}`}
                    onClick={() => removeSavedDocument(idx)}
                    disabled={saving}
                    className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                  >
                    <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}

            {pendingFiles.map((file, idx) => (
              <div
                key={`pending-${idx}-${file.name}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-white"
              >
                <span className="text-xs text-[#16A34A] truncate min-w-0 flex-1">
                  {file.name}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openPendingPreview(file)}
                    className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removePendingFile(idx)}
                    disabled={saving}
                    className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                  >
                    <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </form>
  );
}
